import { useEffect, useRef } from 'react'
import './LoadingScreen.css'

/** The Keenfolks K mark's three strokes, in its 256×256 coordinate space. */
const K_PATHS = [
	'M14,16 H58 V144 L14,188 Z',
	'M196,16 H240 V78 L78,240 H14 L196,58 Z',
	'M241,240 H196 V184 L133,121 L165,89 L241,164 Z',
] as const

/** Canvas backing size; rendered at a quarter of this, so the dots stay crisp on retina. */
const S = 1600
/** The mark's edge length inside the canvas, and its centering offset. */
const MARK = 416
const OFF = (S - MARK) / 2
/** Sampling grid step — one particle per lit cell of the rasterized mark. */
const GAP = 5
/** Converge duration (s) and the settle pause before the loop reseeds. */
const TOTAL = 2.6
const SETTLE = 1.1

interface Particle {
	tx: number
	ty: number
	x: number
	y: number
	d: number
	sp: number
	s: number
}

/** Rasterize the mark offscreen and sample it into particle target points. */
function sampleTargets(): { x: number; y: number }[] {
	const off = document.createElement('canvas')
	off.width = off.height = S
	const c = off.getContext('2d')
	if (!c) return []
	const k = MARK / 256
	c.setTransform(k, 0, 0, k, OFF, OFF)
	// Any opaque colour does: this canvas is never displayed and only its alpha
	// channel is sampled below, so no design token is involved.
	c.fillStyle = '#000'
	for (const d of K_PATHS) c.fill(new Path2D(d))
	const px = c.getImageData(0, 0, S, S).data
	const half = (GAP / 2) | 0
	const pts: { x: number; y: number }[] = []
	for (let y = 0; y < S; y += GAP)
		for (let x = 0; x < S; x += GAP) {
			if (px[((y + half) * S + x + half) * 4 + 3] > 140) pts.push({ x: x + GAP / 2, y: y + GAP / 2 })
		}
	return pts
}

/** Scatter one particle per target on a ring outside the mark, with its own delay, speed, and size. */
const seed = (targets: { x: number; y: number }[]): Particle[] =>
	targets.map((t) => {
		const a = Math.random() * Math.PI * 2
		const r = MARK * (1.15 + Math.random() * 0.6)
		return {
			tx: t.x,
			ty: t.y,
			x: S / 2 + Math.cos(a) * r,
			y: S / 2 + Math.sin(a) * r,
			d: Math.random() * 0.42,
			sp: 0.9 + Math.random() * 0.5,
			s: 2.2 + Math.random() * 1.6,
		}
	})

const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3

/** One frame of the converge: ghost mark darkening beneath, particles flying in on their own eased clocks. */
/**
 * The mark's ink, read from the design token: this canvas is visible, so it has to
 * follow the theme the rest of the shell is painted from.
 *
 * The 2D API takes a colour string and cannot resolve a custom property, so the
 * value is read off the element rather than passed as `var(--ds-…)`. Falls back to
 * `currentColor` — which the canvas inherits from its own computed text colour —
 * so an unresolvable variable still yields a themed colour rather than a literal.
 */
function inkColor(ctx: CanvasRenderingContext2D): string {
	const v = getComputedStyle(ctx.canvas).getPropertyValue('--ds-semantic-color-text').trim()
	return v || getComputedStyle(ctx.canvas).color
}

function drawFrame(ctx: CanvasRenderingContext2D, parts: Particle[], elapsed: number): boolean {
	const prog = Math.min(1, elapsed / TOTAL)
	ctx.clearRect(0, 0, S, S)
	const gp = Math.min(1, prog / 0.78)
	const g = Math.round(237 * (1 - gp ** 4))
	ctx.save()
	ctx.setTransform(MARK / 256, 0, 0, MARK / 256, OFF, OFF)
	ctx.fillStyle = `rgb(${g},${g},${g})`
	for (const d of K_PATHS) ctx.fill(new Path2D(d))
	ctx.restore()
	let settled = 0
	// Read once per frame, not once per particle.
	const ink = inkColor(ctx)
	for (const p of parts) {
		const local = Math.max(0, Math.min(1, ((prog - p.d) / (1 - p.d)) * p.sp))
		const e = easeOutCubic(local)
		const x = p.x + (p.tx - p.x) * e
		const y = p.y + (p.ty - p.y) * e
		ctx.globalAlpha = Math.min(1, e * 2.2) * (prog >= 0.94 ? 0 : 1)
		ctx.fillStyle = ink
		const sz = p.s + (GAP - p.s) * e
		ctx.beginPath()
		ctx.arc(x, y, sz * 0.62, 0, 6.2832)
		ctx.fill()
		if (e > 0.999) settled++
	}
	ctx.globalAlpha = 1
	return settled === parts.length && elapsed > TOTAL + SETTLE
}

/** Reduced motion: no converge loop — the finished mark, drawn once. */
function drawStaticMark(ctx: CanvasRenderingContext2D): void {
	ctx.clearRect(0, 0, S, S)
	ctx.setTransform(MARK / 256, 0, 0, MARK / 256, OFF, OFF)
	ctx.fillStyle = inkColor(ctx)
	for (const d of K_PATHS) ctx.fill(new Path2D(d))
}

/**
 * Full-screen loading state — the Claude Design "dk" project's `dk-loading.html`
 * variant `02 · particles_`: dots scatter on a ring around the Keenfolks mark
 * and converge into it while a ghost of the mark darkens beneath them, looping
 * until the screen unmounts.
 */
export function LoadingScreen() {
	const canvasRef = useRef<HTMLCanvasElement | null>(null)

	useEffect(() => {
		const ctx = canvasRef.current?.getContext('2d')
		if (!ctx) return
		if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
			drawStaticMark(ctx)
			return
		}
		const targets = sampleTargets()
		let parts = seed(targets)
		let start = 0
		let raf = 0
		const frame = (now: number) => {
			if (!start) start = now
			if (drawFrame(ctx, parts, (now - start) / 1000)) {
				start = 0
				parts = seed(targets)
			}
			raf = requestAnimationFrame(frame)
		}
		raf = requestAnimationFrame(frame)
		return () => cancelAnimationFrame(raf)
	}, [])

	return (
		<div className="loading-screen" role="status">
			<span className="sr-only">Loading…</span>
			<canvas ref={canvasRef} className="loading-screen__parts" width={S} height={S} aria-hidden="true" />
		</div>
	)
}
